import { Alert, Button, Card, CardContent, Spinner, buttonVariants, cn } from '@blink/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MapPin, PackageCheck, PackageX, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ErrorAlert, Screen } from '@/components/common';
import { CallButtons } from '@/components/call-bits';
import { LiveDot, StatusBadge, aed } from '@/components/parcel-bits';
import { GoButton, NavAppPicker, useNavApp } from '@/components/route-bits';
import { useI18n } from '@/i18n';
import { ApiClientError, api, type ParcelDetails } from '@/lib/api';
import { useLiveUpdates } from '@/lib/live';

/** Stop details: opens when the courier taps a parcel. Full name and address; numbers stay hidden. */
export function Parcel() {
  const { t, f } = useI18n();
  const navigate = useNavigate();
  const live = useLiveUpdates();
  const { id } = useParams();
  const q = useQuery({ queryKey: ['courier', 'parcel', id], queryFn: () => api<ParcelDetails>(`/courier/parcels/${id}`), retry: false });
  const p = q.data;
  const gone = q.error instanceof ApiClientError && q.error.status === 404;
  const [navApp, setNavApp] = useNavApp();
  const onRoute = p?.status === 'OUT_FOR_DELIVERY' || p?.status === 'IN_TRANSIT';

  return (
    <Screen
      title={
        <>
          <p className="flex items-center gap-3 text-xs text-sidebar-foreground">
            <LiveDot state={live} />
          </p>
          <h1 className="truncate font-mono text-lg font-semibold" data-testid="parcel-awb">{p?.awb ?? '…'}</h1>
        </>
      }
      actions={
        <Button variant="ghost" size="icon" className="order-first text-white hover:bg-white/10 hover:text-white" aria-label={t.parcel.back} onClick={() => navigate('/')} data-testid="back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
      }
    >
      {q.isLoading ? <Spinner /> : null}
      {gone ? (
        <Alert variant="warning" data-testid="parcel-gone">
          {t.parcel.notMine}
        </Alert>
      ) : (
        <ErrorAlert error={q.error} />
      )}
      {p ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={p.status} />
            <span className="text-sm font-semibold" data-testid="parcel-cod">{p.codFils > 0 ? f(t.parcel.cod, { amount: aed(p.codFils) }) : t.parcel.noCod}</span>
            <span className="text-sm text-muted-foreground">· {p.pieceCount === 1 ? t.parcel.onePiece : f(t.parcel.pieces, { count: p.pieceCount })}</span>
          </div>

          {onRoute ? (
            <div className="space-y-2">
              <GoButton shipmentId={p.id} navApp={navApp} />
              <Link to={`/parcels/${p.id}/deliver`} className={cn(buttonVariants({ variant: 'outline' }), 'h-12 w-full border-success text-base text-success')} data-testid="go-deliver">
                <PackageCheck className="h-5 w-5" />
                {t.parcel.deliver}
              </Link>
              <Link to={`/parcels/${p.id}/fail`} className={cn(buttonVariants({ variant: 'outline' }), 'h-12 w-full border-destructive text-base text-destructive')} data-testid="go-fail">
                <PackageX className="h-5 w-5" />
                {t.parcel.notDelivered}
              </Link>
              <NavAppPicker value={navApp} onChange={setNavApp} />
            </div>
          ) : null}

          <Card>
            <CardContent className="space-y-3 pt-6">
              <Row label={t.parcel.customer}>
                <span className="font-semibold" data-testid="parcel-customer">{p.consigneeName}</span>
              </Row>
              <Row label={t.parcel.phone}>
                <span className="flex items-center gap-1.5 font-mono" data-testid="parcel-phone">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {p.phone}
                </span>
              </Row>
              {p.altPhone ? <Row label={t.parcel.altPhone}><span className="font-mono">{p.altPhone}</span></Row> : null}
              {onRoute ? <CallButtons shipmentId={p.id} calls={p.calls} hasAlt={!!p.altPhone} /> : <p className="text-xs text-muted-foreground">{t.parcel.callHint}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <Row label={t.parcel.address}>
                <span data-testid="parcel-address">{[p.addressText, p.city].filter(Boolean).join(', ') || '—'}</span>
              </Row>
              <Row label={t.parcel.district}>{p.district ?? '—'}</Row>
              <Row label={t.parcel.pin}>
                {p.pin ? (
                  <a
                    className="inline-flex items-center gap-1 font-semibold text-primary underline"
                    href={`https://www.google.com/maps/search/?api=1&query=${p.pin.latitude},${p.pin.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MapPin className="h-4 w-4" />
                    {t.parcel.openMap}
                  </a>
                ) : (
                  <span className="space-y-1">
                    <span className="block text-muted-foreground">{t.parcel.noPin}</span>
                    {/* no pin: let the courier search the written address (the call to the customer gives the door) */}
                    <a
                      className="inline-flex items-center gap-1 font-semibold text-primary underline"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([p.addressText, p.district, p.city].filter(Boolean).join(', '))}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="search-address"
                    >
                      <MapPin className="h-4 w-4" />
                      {t.parcel.searchAddress}
                    </a>
                  </span>
                )}
              </Row>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <h2 className="font-semibold">{t.parcel.details}</h2>
              <Row label={t.parcel.merchant}>{p.merchant}</Row>
              {p.weightGrams ? <Row label={t.parcel.weight}>{(p.weightGrams / 1000).toFixed(1)} kg</Row> : null}
              {p.deliveryType ? <Row label={t.parcel.deliveryType}>{p.deliveryType}</Row> : null}
              {p.timeSlot ? <Row label={t.parcel.timeSlot}>{p.timeSlot}</Row> : null}
              {p.description ? <Row label={t.parcel.description}>{p.description}</Row> : null}
              <Row label={t.parcel.attempts}>{p.attemptCount}</Row>
              <h3 className="pt-2 text-sm font-semibold">{t.parcel.piecesTitle}</h3>
              <ul className="space-y-1" data-testid="parcel-pieces">
                {p.pieces.map((piece) => (
                  <li key={piece.barcode} className="flex justify-between rounded-md bg-muted px-3 py-2 text-sm">
                    <span className="font-mono">{piece.label}</span>
                    <span className="text-muted-foreground">{t.parcel.pieceState[piece.state]}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      ) : null}
    </Screen>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}
